/**
 * Quest Board View
 *
 * Shows quests available for acceptance from a market's quest board.
 * Active/completed quests are tracked exclusively via the bottom QuestDock.
 */

import { useState, useEffect } from 'react';
import { useGameStore } from '@/state';
import { marketManager, questManager, questBoardManager } from '@/systems';
import { QuestDefinition } from '@/types';
import { ScrollableView } from '@/components/layout/ScrollableView';
import { EmptyState } from '@/components/layout/EmptyState';

interface QuestCardProps {
  quest: QuestDefinition;
  marketId: string;
}

function QuestCard({ quest, marketId }: QuestCardProps) {
  const handleAccept = () => {
    const success = questManager.acceptQuest(quest);
    if (success) {
      // Remove from quest board cache so it doesn't reappear;
      // a replacement will be generated after the refresh interval
      if (quest.category === 'buddy_quest') {
        questBoardManager.removeQuestFromBuddyCache(marketId, quest.id);
      } else if (quest.expiresAt) {
        // Procedural quests have expiresAt; curated quests don't
        questBoardManager.removeQuestFromCache(marketId, quest.id);
      }
    } else {
      console.warn(`Failed to accept quest ${quest.id}`);
    }
  };

  const borderStyle = quest.isTutorial
    ? 'border-yellow-600 bg-yellow-950/10'
    : 'border-gray-700 bg-gray-800';

  return (
    <div className={`rounded-lg p-5 border-2 ${borderStyle}`}>
      {/* Tutorial quest header */}
      {quest.isTutorial && (
        <div className="flex items-center gap-2 mb-2">
          <span className="text-yellow-400 text-xl">⭐</span>
          <span className="text-sm font-bold text-yellow-400 uppercase tracking-wide">
            Story Quest
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className={`text-lg font-bold ${quest.isTutorial ? 'text-yellow-100' : 'text-white'}`}>
            {quest.name}
          </h3>
          <p className="text-gray-400 text-sm">{quest.description}</p>
          {quest.category && !quest.isTutorial && (
            <span className="inline-block mt-1 text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded capitalize">
              {quest.category}
            </span>
          )}
        </div>
        <div className="text-2xl">
          {quest.isTutorial ? '⭐' : '📜'}
        </div>
      </div>

      {/* Objectives */}
      <div className="space-y-2 mb-4">
        {quest.objectives.map((objective) => (
          <div key={objective.id} className="bg-gray-900 rounded p-3">
            <p className="text-sm text-gray-300">{objective.description}</p>
          </div>
        ))}
      </div>

      {/* Rewards */}
      <div className="bg-gray-900 rounded p-3 mb-4">
        <p className="text-xs text-gray-400 mb-2">Rewards:</p>
        <div className="flex flex-wrap gap-2">
          {quest.rewards.map((reward, idx) => (
            <span
              key={idx}
              className="inline-block text-xs bg-yellow-900/30 text-yellow-300 px-2 py-1 rounded"
            >
              {reward.description}
            </span>
          ))}
        </div>
      </div>

      {/* Accept button */}
      <button
        onClick={handleAccept}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-bold transition-colors cursor-pointer"
      >
        Accept Quest
      </button>
    </div>
  );
}

interface QuestBoardViewProps {
  marketId: string;
}

export function QuestBoardView({ marketId }: QuestBoardViewProps) {
  const market = marketManager.getMarket(marketId);

  // Subscribe to full playerState so quest board re-renders when ANY unlock condition changes
  // (evaluateQuestUnlock checks: level, currency, stats, equipment, buddies, zones, quests, etc.)
  useGameStore((state) => state.playerState);

  // Fetch available quests - will update when any playerState field changes
  const availableQuests = marketManager.getAvailableQuests(marketId);

  // Refresh timer for procedural quests
  const [timeUntilRefresh, setTimeUntilRefresh] = useState(() =>
    questBoardManager.getTimeUntilRefresh(marketId)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = questBoardManager.getTimeUntilRefresh(marketId);
      setTimeUntilRefresh(remaining);
    }, 1000);
    return () => clearInterval(interval);
  }, [marketId]);

  if (!market) {
    return <EmptyState emoji="📋" message="Market not found." />;
  }

  // Sort quests: tutorial quests first, then by priority (if set), then alphabetically
  const sortedQuests = [...availableQuests].sort((a, b) => {
    // Tutorial quests always come first
    if (a.isTutorial && !b.isTutorial) return -1;
    if (!a.isTutorial && b.isTutorial) return 1;

    // Then sort by priority (higher priority = earlier in list)
    const priorityA = a.priority ?? 0;
    const priorityB = b.priority ?? 0;
    if (priorityA !== priorityB) return priorityB - priorityA;

    // Finally, alphabetically by name
    return a.name.localeCompare(b.name);
  });

  // Group quests by type (render order: tutorial → progression → buddy → procedural)
  const tutorialQuests = sortedQuests.filter((q) => q.isTutorial);
  const progressionQuests = sortedQuests.filter((q) => !q.isTutorial && q.category === 'progression');
  const buddyQuests = sortedQuests.filter((q) => !q.isTutorial && q.category === 'buddy_quest');
  const regularQuests = sortedQuests.filter((q) => !q.isTutorial && q.category !== 'buddy_quest' && q.category !== 'progression');

  return (
    <ScrollableView>
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">📋 Quest Board</h1>
        <p className="text-gray-400">{market.name}</p>
        {market.npcName && (
          <p className="text-gray-500 text-sm">Quests from {market.npcName}</p>
        )}
      </div>

      {/* Quest List */}
      {availableQuests.length === 0 ? (
        <EmptyState
          emoji="📋"
          message="No quests available right now."
          subMessage={`Quest board refreshes every ${market.questBoard.refreshInterval} hours.`}
        />
      ) : (
        <div className="space-y-6">
          {/* Tutorial/Story Quests Section */}
          {tutorialQuests.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">⭐</span>
                <h2 className="text-xl font-bold text-yellow-400">Story Quests</h2>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                Follow these quests to learn the basics and unlock new features.
              </p>
              <div className="grid grid-cols-1 gap-4">
                {tutorialQuests.map((quest) => (
                  <QuestCard
                    key={quest.id}
                    quest={quest}
                    marketId={marketId}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Progression Quests Section */}
          {progressionQuests.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3 mt-6">
                <span className="text-2xl">📈</span>
                <h2 className="text-xl font-bold text-blue-300">Progression Quests</h2>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                Build your skills and grow your fishing empire.
              </p>
              <div className="grid grid-cols-1 gap-4">
                {progressionQuests.map((quest) => (
                  <QuestCard
                    key={quest.id}
                    quest={quest}
                    marketId={marketId}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Buddy Quests Section */}
          {buddyQuests.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3 mt-6">
                <span className="text-2xl">🤝</span>
                <h2 className="text-xl font-bold text-purple-300">Buddy Quests</h2>
              </div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-400">
                  Automated quests for your fishing buddies and haulers.
                </p>
                {timeUntilRefresh > 0 && (
                  <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                    Refreshes in {Math.ceil(timeUntilRefresh / 60000)}m
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 gap-4">
                {buddyQuests.map((quest) => (
                  <QuestCard
                    key={quest.id}
                    quest={quest}
                    marketId={marketId}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Regular Quests Section */}
          {regularQuests.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3 mt-6">
                <span className="text-2xl">📜</span>
                <h2 className="text-xl font-bold text-white">Bounties & Challenges</h2>
              </div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-400">
                  Complete these for extra rewards and progression.
                </p>
                {timeUntilRefresh > 0 && (
                  <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                    New quests in {Math.ceil(timeUntilRefresh / 60000)}m
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 gap-4">
                {regularQuests.map((quest) => (
                  <QuestCard
                    key={quest.id}
                    quest={quest}
                    marketId={marketId}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </ScrollableView>
  );
}
